import { CanvasStatus } from "./types";
import { IInputManager } from "./types/input-manager";
import Canvas from "./models/canvas";
import { ICanvas } from "./types/canvas";
import Palette from "./models/palette";
import IPlayer from "./models/player";
import InitialState from "./states/initial-state";
import { IState } from "./types/state";
import { IGame } from "./types/game";
import { GameParameters } from "./types/game-parameters";
import { Effect } from "./types/effects";
import { Stat } from "./models/stat";
import { CS_STAT_TYPES, CS_StatType } from "./models/stat";
import { IAudioManager } from "./types/audio-manager";
import { Packet } from "../protocol/packets";
import { PacketTypes } from "../protocol/types";
import { IMultiplayerAdapter } from "./interfaces/IMultiplayerAdapter";
import { KeyStatus } from "./types/stats";
import Overmind from "./executor/overmind";
import Executor from "./executor/executor";

export interface PacketWatcher {
  onPacket(packet: Packet): void;
}

export default class Game implements IGame {
  screen: HTMLCanvasElement;
  context: CanvasRenderingContext2D;
  canvas: ICanvas;
  player: IPlayer;
  input: IInputManager;
  width: number;
  height: number;
  scale: number;
  status: CanvasStatus.PLAYING;
  lastFrameTime: number;
  palette: Palette;
  audioManager: IAudioManager;
  padding: number;
  playback: boolean;
  timeout: any;
  tickSpeed: number = 1 / 100;
  effects: Effect[] = [];
  stats: Record<CS_StatType, Stat>;
  comboMin: number = 2;
  onLinesCleared?: (
    lines: number,
    complexity: number,
    isClean: boolean,
    holePositions: number[]
  ) => void;
  /** For multiplayer: whether the game has actually started (countdown finished). Undefined for single player games. */
  multiplayerStarted?: boolean;
  /** Whether this is a multiplayer game */
  isMultiplayer: boolean = false;
  /** Callback to process pending garbage packets (for multiplayer). Called before state.update() to match original sequential execution. */
  processPendingAttacks?: () => void;
  /** Packet queue (matches original Game::stack) */
  private packetQueue: Packet[] = [];
  /** Single player mode (matches original game->single) */
  public single: boolean = false;
  /** Network mode (matches original game->network) */
  public network: boolean = true;
  /** Level up enabled (matches original game->level_up) */
  public levelUp: boolean = false;
  /** Game end type (matches original game_end enum) */
  public gameEnd?: "END_TIME" | "END_FRAG" | "END_LINES";
  /** Game end value (matches original game_end_value) */
  public gameEndValue?: number;
  /** Game terminated flag (matches original game->terminated) */
  public terminated: boolean = false;
  /** Callback when single player game ends (for high score checking) */
  public onSinglePlayerGameEnd?: (
    score: number,
    level: number,
    lines: number
  ) => void;
  /** Overmind for managing parallel executors */
  public overmind: Overmind;
  /** Main executor for game states (matches original Canvas::myself) */
  public mainExecutor?: Executor;
  /** Paused state (matches original game->paused) */
  public paused: boolean = false;
  /** Countdown timer (matches original game->delay_start) - stored on game instance, not React state */
  public delayStart: number = 0;
  /** Remote game flag - skip input processing and packet sending */
  isRemote: boolean = false;
  /** Remote player ID for tracking */
  remotePlayerId?: string;
  /** Block pack preference for this player's game instance */
  blockPack?: string;
  /** Canvas pack preference for this player's game instance */
  canvasPack?: string;
  /** Packet watchers for specific packet types */
  private packetWatchers: Map<PacketTypes, Set<PacketWatcher>> = new Map();

  constructor(
    /* screen: HTMLCanvasElement, */ player: IPlayer,
    audioManager: IAudioManager,
    gameSeed?: number,
  ) {
    this.player = player;
    this.canvas = new Canvas(gameSeed);
    this.canvas.game = this;
    this.palette = this.getPalette();
    this.audioManager = audioManager;
    this.audioManager.preloadGameSounds();
    this.stats = CS_STAT_TYPES.reduce((acc, stat) => {
      acc[stat] = new Stat();
      return acc;
    }, {} as Record<CS_StatType, Stat>);
    this.overmind = new Overmind();
    this.init();
  }

  init() {
    this.audioManager.setSfxVolume(0.1);
  }

  private getPalette(): Palette {
    let palette = new Palette(),
      colors = [
        "#FF7D00",
        "#00E1FF",
        "#FF0000",
        "#FF00FF",
        "#FFFF00",
        "#00FF00",
        "#2828FF",
        "#AAAAAA",
        "#AAAAAA",
        "#404040",
      ];
    for (let i = 0; i < colors.length; i++) {
      palette.setHexColor(i, colors[i]);
    }
    return palette;
  }

  start(params: GameParameters = {}): void {
    if (params.single !== undefined) {
      this.single = params.single;
    }
    if (params.network !== undefined) {
      this.network = params.network;
    }
    if (params.levelUp !== undefined) {
      this.levelUp = params.levelUp;
    }
    if (params.gameEnd !== undefined) {
      this.gameEnd = params.gameEnd;
    }
    if (params.gameEndValue !== undefined) {
      this.gameEndValue = params.gameEndValue;
    }

    if (this.player.isLocal()) {
      if (this.mainExecutor && this.mainExecutor.getTopState()) {
        const topState = this.mainExecutor.getTopState();
        const stateName = topState?.toString() || "";
        if (stateName === "Initial State" || stateName === "Normal") {
          console.log(
            `[Game.start] Game already initialized (executor has ${stateName}), not restarting`
          );
          return;
        }
      }

      this.canvas.waitDownload = false;
      console.log(
        `[Game.start] Set local canvas.waitDownload = false before InitialState`
      );

      if (this.mainExecutor && this.mainExecutor.getTopState()) {
        const topState = this.mainExecutor.getTopState();
        const stateName = topState?.toString() || "";
        if (stateName === "Initial State" || stateName === "Normal") {
          console.log(
            `[Game.start] Game already initialized (executor has ${stateName}), not restarting`
          );
          return;
        }
      }

      if (!this.mainExecutor) {
        this.mainExecutor = new Executor();
        this.overmind.start(this.mainExecutor);
        console.log(
          "[Game.start] Created main executor and started it in overmind"
        );
      }

      console.log("[Game.start] Adding InitialState to executor");
      const initialState = new InitialState(this);
      this.mainExecutor.add(initialState);
      console.log(`[Game.start] Added InitialState to executor`);
    }
    this.lastFrameTime = new Date().getTime();
  }

  stop(): void {
    clearTimeout(this.timeout);
    this.timeout = null;
  }

  get state(): IState | null {
    return this.mainExecutor?.getTopState() || null;
  }

  popState(callingState?: IState): void {
    const stateToMark = callingState || this.state;
    if (stateToMark && stateToMark.markDone) {
      stateToMark.markDone();
    }
  }

  pushState(state: IState): void {
    if (this.mainExecutor) {
      this.mainExecutor.add(state);

      if (state.setParentExecutor) {
        state.setParentExecutor(this.mainExecutor);
      }
    }
  }

  /**
   * Peek at packet queue (matches original Game::peekpacket())
   * @param type Packet type to peek for, or undefined to peek at any packet
   * @returns Packet if found, null otherwise
   */
  peekPacket(type?: PacketTypes): Packet | null {
    if (this.packetQueue.length === 0) {
      return null;
    }
    const front = this.packetQueue[0];
    if (!type || front.packetId === type) {
      return front;
    }
    return null;
  }

  /**
   * Find packet in queue by type (for priority processing like P_ENDGAME)
   * Returns the index of the packet, or -1 if not found
   */
  findPacketIndex(type: PacketTypes): number {
    return this.packetQueue.findIndex((p) => p.packetId === type);
  }

  /**
   * Remove packet from queue (matches original Game::removepacket())
   */
  removePacket(): void {
    if (this.packetQueue.length > 0) {
      const removed = this.packetQueue.shift();
      if (removed?.packetId === PacketTypes.DOWNLOAD_STATE) {
        console.log(
          `[Game.removePacket] Removed P_DOWNLOAD from queue, remaining length=${this.packetQueue.length}`
        );
      }
    } else {
      console.warn(`[Game.removePacket] Called but queue is empty`);
    }
  }

  /**
   * Add packet to queue (matches original Game::stackpacket())
   * @param packet Packet to add to queue
   */
  stackPacket(packet: Packet): void {
    if (packet.packetId === PacketTypes.DOWNLOAD_STATE) {
      this.notifyWatchers(packet);

      return;
    }

    this.packetQueue.push(packet);
    this.notifyWatchers(packet);
  }

  /**
   * Add packet watcher for specific packet type
   * Reference: quadra/source/net_stuff.cc (Net_client::addwatch)
   */
  addWatch(packetType: PacketTypes, watcher: PacketWatcher): void {
    if (!this.packetWatchers.has(packetType)) {
      this.packetWatchers.set(packetType, new Set());
    }
    this.packetWatchers.get(packetType)!.add(watcher);
  }

  /**
   * Remove packet watcher for specific packet type
   * Reference: quadra/source/net_stuff.cc (Net_client::removewatch)
   */
  removeWatch(packetType: PacketTypes, watcher: PacketWatcher): void {
    const watchers = this.packetWatchers.get(packetType);
    if (watchers) {
      watchers.delete(watcher);
    }
  }

  /**
   * Notify watchers when packet is stacked
   */
  private notifyWatchers(packet: Packet): void {
    const watchers = this.packetWatchers.get(packet.packetId);
    if (packet.packetId === PacketTypes.DOWNLOAD_STATE) {
      console.log(
        `[Game.notifyWatchers] P_DOWNLOAD packet routed directly to watchers (not queued), watchers: ${
          watchers ? watchers.size : 0
        }`
      );
      if (watchers && watchers.size > 0) {
        watchers.forEach((watcher) => {
          console.log(
            `[Game.notifyWatchers] Calling watcher.onPacket() for P_DOWNLOAD`
          );
          watcher.onPacket(packet);
        });
      } else {
        console.log(
          `[Game.notifyWatchers] No watchers for P_DOWNLOAD (waitDownload=false, already processed), packet ignored`
        );
      }
    } else {
      if (watchers) {
        watchers.forEach((watcher) => watcher.onPacket(packet));
      }
    }
  }

  /**
   * Multiplayer adapter - provides networking functionality without coupling to React.
   * Matches original C++ architecture where game logic calls net->sendtcp() and game->net_list
   * without knowing about the underlying implementation.
   */
  private multiplayerAdapter?: IMultiplayerAdapter;

  /**
   * Set multiplayer adapter (dependency injection)
   */
  setMultiplayerAdapter(adapter: IMultiplayerAdapter | undefined): void {
    this.multiplayerAdapter = adapter;
  }

  /** Send stamp block packet to server (for multiplayer) */
  sendStampBlock(): void {
    if (this.multiplayerAdapter?.sendStampBlock && this.canvas.bloc) {
      this.multiplayerAdapter.sendStampBlock(
        this.canvas.bloc.x,
        this.canvas.bloc.y,
        this.canvas.bloc.rotation,
        this.canvas.bloc.quel
      );
    }
  }

  /** Notify that player has died (for multiplayer) */
  notifyDeath(thenGone: boolean): void {
    if (this.multiplayerAdapter?.notifyDeath) {
      const lastAttackerNumPlayer = this.canvas.lastAttackerNumPlayer;
      console.log(
        `[Game.notifyDeath] Called: thenGone=${thenGone}, lastAttackerNumPlayer=${
          lastAttackerNumPlayer ?? "undefined"
        }`
      );
      this.multiplayerAdapter.notifyDeath(thenGone, lastAttackerNumPlayer);
    } else {
      console.warn(
        `[Game.notifyDeath] multiplayerAdapter.notifyDeath not available`
      );
    }
  }

  /** Get last attacker player number (for frag assignment) */
  getLastAttackerNumPlayer(): number | undefined {
    return this.canvas.lastAttackerNumPlayer;
  }

  /** Get player by connection ID (for frag assignment) */
  getPlayer(
    connectionId: string
  ): { name: string; frags: number; deaths: number; team: number } | undefined {
    return this.multiplayerAdapter?.getPlayerInfo?.(connectionId);
  }

  /** Get current syncpoint (for round restart) */
  getSyncpoint():
    | "playing"
    | "waitForWinner"
    | "waitForRestart"
    | "last"
    | undefined {
    return this.multiplayerAdapter?.getSyncpoint?.();
  }

  /** Get current game mode */
  getGameMode():
    | "standard"
    | "survivor"
    | "sprint"
    | "hot_potato"
    | "team"
    | undefined {
    return this.multiplayerAdapter?.getGameMode?.() ?? "standard";
  }

  /** Get current player's ID (for multiplayer packet matching) */
  getConnectionId(): string | undefined {
    return this.multiplayerAdapter?.getConnectionId?.();
  }

  /** Check if game should send moves (for multiplayer) */
  getWantsMoves(): boolean {
    return this.isMultiplayer && !this.isRemote;
  }

  /** Get multiplayer adapter (for sending moves/attacks) */
  getMultiplayerAdapter(): any {
    return this.multiplayerAdapter;
  }

  /** Send client state to server (for syncpoint transitions) */
  sendClientState(
    state: "playing" | "waitForWinner" | "waitForRestart" | "last"
  ): void {
    this.multiplayerAdapter?.sendClientState?.(state);
  }

  /** Notify server that this player survived the round (P_CLIENTFIRST_FRAG) */
  sendClientFirstFrag(): void {
    this.multiplayerAdapter?.sendClientFirstFrag?.();
  }

  /** Restart player (for round restart) */
  restartPlayer(): void {
    if (this.input) {
      this.input.keyLeft = KeyStatus.NONE;
      this.input.keyRight = KeyStatus.NONE;
      this.input.keyDown = KeyStatus.NONE;
      this.input.keyRotate = KeyStatus.NONE;
      this.input.keyRotateTwice = KeyStatus.NONE;
      this.input.keyDrop = KeyStatus.NONE;
    }

    this.multiplayerAdapter?.sendRespawnRequest?.();

    this.canvas.restart();

    const startLevel = this.multiplayerAdapter?.getStartLevel?.() || 1;
    this.canvas.level = startLevel;

    this.canvas.status = CanvasStatus.PLAYING;

    this.sendClientState("playing");
  }

  shouldRenderPieces(): boolean {
    return true;
  }

  giveLine(): void {
    if (!this.canvas.depth) {
      return;
    }

    let scoreAdd: number = 0,
      moveValue: number = (this.canvas.depth << 8) + this.canvas.complexity;

    if (this.canvas.sendForClean) {
      if (moveValue > this.canvas.bestClean) {
        this.canvas.bestClean = moveValue;
      }
    }

    switch (this.canvas.depth) {
      case 1:
        scoreAdd = 250;
        break;
      case 2:
        scoreAdd = 500;
        break;
      case 3:
        scoreAdd = 1000;
        break;
      case 4:
        scoreAdd = 2000;
        break;
      default:
        scoreAdd = 200 * this.canvas.depth * this.canvas.depth;
        break;
    }

    if (this.canvas.complexity > 1) {
      scoreAdd +=
        200 * (this.canvas.complexity - 1) * (this.canvas.complexity - 1);
    }

    if (this.canvas.sendForClean) {
      let cleanPoints;
      if (this.canvas.depth <= 4) cleanPoints = this.canvas.depth * 1250;
      else cleanPoints = this.canvas.depth * this.canvas.depth * 500;
      scoreAdd += cleanPoints;
    }

    scoreAdd += (scoreAdd / 10) * this.canvas.level;
    this.stats["SCORE"].add(scoreAdd);

    if (this.stats["LINESTOT"]) {
      this.stats["LINESTOT"].add(this.canvas.depth);
    }
    if (this.stats["LINESCUR"]) {
      this.stats["LINESCUR"].add(this.canvas.depth);
    }

    let clearIndex = this.canvas.depth - 1;
    if (clearIndex >= 14) {
      const highClearStat = `CLEAR${
        14 + (clearIndex - 14)
      }` as keyof typeof this.stats;
      if (this.stats[highClearStat]) {
        this.stats[highClearStat].add(1);
      }
      clearIndex = 14;
    }

    if (clearIndex >= 0) {
      const clearStat = `CLEAR${String(clearIndex).padStart(
        2,
        "0"
      )}` as keyof typeof this.stats;
      if (this.stats[clearStat]) {
        this.stats[clearStat].add(1);
      }
    }

    if (this.canvas.complexity > 0) {
      const comboIndex = this.canvas.complexity - 1;
      if (comboIndex >= 0 && comboIndex <= 20) {
        const comboStat = `COMBO${String(comboIndex).padStart(
          2,
          "0"
        )}` as keyof typeof this.stats;
        if (this.stats[comboStat]) {
          this.stats[comboStat].add(1);
        }
      }
    }

    if (this.levelUp && this.single) {
      const linesCleared =
        this.stats["LINESCUR"]?.getValue() ||
        this.stats["LINESTOT"]?.getValue() ||
        0;
      const levelUpThreshold = this.canvas.level * 15;
      if (linesCleared >= levelUpThreshold) {
        this.canvas.level++;

        this.canvas.calcSpeed();

        console.log(
          `[Game] Level up! New level: ${this.canvas.level}, speed: ${this.canvas.speed}`
        );

        const LevelUpState = require("./states/level-up-state").default;
        const tmp = new Executor(true);
        tmp.add(new LevelUpState(this));
        this.overmind.start(tmp);

        if (this.stats["LINESCUR"]) {
          this.stats["LINESCUR"].setValue(0);
        }
      }
    }

    let i: number = this.canvas.depth - 1;
    let enough: boolean = this.canvas.depth >= this.comboMin;

    if (!i) enough = false;

    const holePositions: number[] = [];
    if (i > 0 && enough && this.canvas.moved) {
      for (let lineIdx = 0; lineIdx < i; lineIdx++) {
        let holeMask = 0;
        const movedRow = this.canvas.moved[lineIdx];
        for (let col = 4; col < 14; col++) {
          holeMask <<= 1;
          if (movedRow && movedRow[col]) {
            holeMask |= 1;
          }
        }
        holePositions.push(holeMask);
      }
    }

    if (i > 0 && enough && this.onLinesCleared) {
      this.onLinesCleared(
        i,
        this.canvas.complexity,
        this.canvas.sendForClean,
        holePositions
      );
    }

    this.canvas.depth = 0;
    this.canvas.complexity = 0;
    this.canvas.sendForClean = false;
  }

  /**
   * Step the game once (matches original overmind.step() -> Executor::step())
   * This is called from the main requestAnimationFrame loop with time accumulation
   * to match the original behavior where game logic runs at 100fps (10ms intervals)
   * but rendering happens at video frame rate.
   *
   * Reference: quadra/source/quadra.cc:425-446 (time accumulation loop)
   * Reference: quadra/source/overmind.cc:53-74 (overmind.step())
   * Reference: quadra/source/overmind.cc:106-124 (Executor::step())
   */
  step(): void {
    if (this.isRemote) {
      if (this.processPendingAttacks) {
        this.processPendingAttacks();
      }
      this.overmind.step();
      return;
    }

    if (!this.mainExecutor) {
      console.error("[Game.step] No main executor, cannot continue");
      return;
    }

    if (this.processPendingAttacks) {
      this.processPendingAttacks();
    }

    this.overmind.step();
  }

  /**
   * @deprecated Use step() instead. This method is kept for backwards compatibility
   * but the loop is now managed by the React component's requestAnimationFrame loop.
   */
  private loop() {
    this.step();
  }
}
